import { useState, useEffect, useCallback } from 'react';
import { Spin, Tag, Button, message, Input, Checkbox, Switch, Select } from 'antd';
import { mobile } from '../../services/api';
import './mobile.css';

export default function MobileInspection() {
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [checklist, setChecklist] = useState([]);
  const [resultNote, setResultNote] = useState('');
  const [creating, setCreating] = useState(false);

  const loadList = useCallback(async () => {
    try {
      const [listRes, tplRes] = await Promise.all([
        mobile.inspections(),
        mobile.inspectionTemplates(),
      ]);
      setList(listRes.data || []);
      setTemplates(tplRes.data || []);
    } catch (err) {
      message.error(err.message || '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (insId) => {
    try {
      const res = await mobile.inspectionDetail(insId);
      setDetail(res.data);
      setChecklist(res.data.checklist || []);
    } catch (err) {
      message.error(err.message || '加载详情失败');
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  const openTask = (insId) => {
    setActiveId(insId);
    loadDetail(insId);
  };

  const toggleItem = (idx, field, value) => {
    setChecklist((prev) => prev.map((item, i) => {
      if (i !== idx) return item;
      const updated = { ...item, [field]: value };
      if (field === 'checked' && value) updated.abnormal = false;
      return updated;
    }));
  };

  const handleSubmit = async () => {
    const unchecked = checklist.filter((i) => !i.checked);
    if (unchecked.length > 0) {
      message.warning('请完成所有检查项');
      return;
    }
    try {
      await mobile.submitInspection(activeId, { checklist, result_note: resultNote });
      message.success('巡检已提交');
      setActiveId(null);
      setDetail(null);
      loadList();
    } catch (err) {
      message.error(err.message || '提交失败');
    }
  };

  const handleCreate = async (templateKey) => {
    setCreating(true);
    try {
      const res = await mobile.createInspection({ template_key: templateKey });
      message.success('巡检任务已创建');
      await loadList();
      openTask(res.data.id);
    } catch (err) {
      message.error(err.message || '创建失败');
    } finally {
      setCreating(false);
    }
  };

  if (loading) return <Spin style={{ display: 'block', margin: '60px auto' }} />;

  if (activeId && detail) {
    return (
      <div>
        <Button type="link" size="small" onClick={() => { setActiveId(null); setDetail(null); }} style={{ padding: 0, marginBottom: 8 }}>
          ← 返回列表
        </Button>
        <div className="mobile-card">
          <div style={{ fontWeight: 600, fontSize: 16 }}>{detail.title}</div>
          <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>{detail.task_no}</div>
          {detail.space_name && <div style={{ fontSize: 13, color: '#666', marginTop: 6 }}>位置：{detail.space_name}</div>}
        </div>

        <div className="mobile-card">
          <div style={{ fontWeight: 600, marginBottom: 12 }}>检查项</div>
          {checklist.map((item, idx) => (
            <div key={idx} className="checklist-item">
              <div style={{ flex: 1 }}>
                <Checkbox
                  checked={item.checked}
                  onChange={(e) => toggleItem(idx, 'checked', e.target.checked)}
                >
                  {item.item}
                </Checkbox>
              </div>
              {item.checked && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                  <span style={{ color: '#999' }}>异常</span>
                  <Switch size="small" checked={item.abnormal} onChange={(v) => toggleItem(idx, 'abnormal', v)} />
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mobile-card">
          <div style={{ marginBottom: 8, fontSize: 13 }}>备注</div>
          <Input.TextArea rows={3} value={resultNote} onChange={(e) => setResultNote(e.target.value)} placeholder="巡检情况补充说明" />
        </div>

        <Button type="primary" block size="large" onClick={handleSubmit}>
          提交巡检
        </Button>
      </div>
    );
  }

  const pending = list.filter((i) => i.status === '待巡检');
  const done = list.filter((i) => i.status === '已完成');

  return (
    <div>
      <div className="mobile-card">
        <div style={{ fontWeight: 600, marginBottom: 10 }}>新建巡检</div>
        <Select
          placeholder="选择巡检模板"
          style={{ width: '100%' }}
          loading={creating}
          onChange={handleCreate}
          options={templates.map((t) => ({ value: t.key, label: `${t.title}（${t.frequency}）` }))}
        />
      </div>

      <div style={{ fontWeight: 600, margin: '8px 4px' }}>待巡检 ({pending.length})</div>
      {pending.length === 0 && (
        <div className="mobile-card" style={{ textAlign: 'center', color: '#999', padding: 24 }}>暂无待巡检任务</div>
      )}
      {pending.map((item) => (
        <div key={item.id} className="mobile-card" onClick={() => openTask(item.id)} style={{ cursor: 'pointer' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ fontWeight: 600 }}>{item.title}</div>
            <Tag color="orange">待巡检</Tag>
          </div>
          <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>{item.task_no}</div>
        </div>
      ))}

      {done.length > 0 && (
        <>
          <div style={{ fontWeight: 600, margin: '12px 4px 8px' }}>已完成</div>
          {done.slice(0, 5).map((item) => (
            <div key={item.id} className="mobile-card">
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>{item.title}</div>
                <Tag color="green">已完成</Tag>
              </div>
              <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                {item.inspector} · 异常 {item.abnormal_count} 项
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
